import { useEffect, useMemo, useState } from "react";
import { getSessionToken } from "../hooks/useSession";
import { Button } from "./ui/Button";
import { Skeleton } from "./ui/Skeleton";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface Artifact {
  fileName: string;
  size: number;
  createdAt: string;
  updatedAt: string;
}

interface ArtifactRun {
  runId: string;
  updatedAt: string;
  artifacts: Artifact[];
  account?: {
    email: string;
    username: string;
    password: string;
    inviteUrl?: string | null;
    createdAt: string;
  } | null;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function sortByRecency(runs: ArtifactRun[]) {
  return [...runs].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

function artifactKey(runId: string, fileName: string) {
  return `${runId}/${fileName}`;
}

async function fetchArtifactImage(runId: string, fileName: string, token: string) {
  const response = await fetch(
    `${API_BASE}/api/reagan/artifacts/${encodeURIComponent(runId)}/${encodeURIComponent(fileName)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    throw new Error(`Failed to load ${fileName}`);
  }

  return URL.createObjectURL(await response.blob());
}

export function RunArtifacts() {
  const [runs, setRuns] = useState<ArtifactRun[]>([]);
  const [images, setImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const token = useMemo(() => getSessionToken(), []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("You need to log in before viewing run artifacts.");
      return;
    }
    const authToken = token;

    let cancelled = false;
    const objectUrls: string[] = [];

    async function loadArtifacts() {
      setLoading(true);
      setError(null);
      setRuns([]);
      setImages({});

      try {
        const response = await fetch(`${API_BASE}/api/reagan/artifacts`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Artifact list failed with ${response.status}`);
        }

        const json = await response.json();
        // Server already sorts by recency; re-sort defensively so the UI
        // stays correct even if that ever changes.
        const nextRuns = sortByRecency((json.data ?? []) as ArtifactRun[]);
        if (cancelled) return;
        setRuns(nextRuns);

        const nextImages: Record<string, string> = {};
        for (const run of nextRuns) {
          for (const artifact of run.artifacts) {
            const key = artifactKey(run.runId, artifact.fileName);
            const objectUrl = await fetchArtifactImage(run.runId, artifact.fileName, authToken);
            objectUrls.push(objectUrl);
            nextImages[key] = objectUrl;
            if (!cancelled) setImages({ ...nextImages });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Unable to load run artifacts.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadArtifacts();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [token, retryCount]);

  useEffect(() => {
    if (expandedRunId && !runs.some((run) => run.runId === expandedRunId)) {
      setExpandedRunId(null);
    }
  }, [expandedRunId, runs]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8 text-content-primary">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent mb-2">ReaganAI</p>
            <h1 className="text-3xl font-bold text-content-primary">Run Artifacts</h1>
            <p className="text-content-secondary mt-2">
              Screenshots captured by ReaganAI automation runs.
            </p>
          </div>
        </div>

        {loading && (
          <div className="grid gap-5 md:grid-cols-2" role="status" aria-busy="true" aria-label="Loading run artifacts">
            {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-48 rounded-2xl" />)}
          </div>
        )}

        {error && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-status-rejected-border bg-status-rejected-bg p-6 text-status-rejected-text">
            <span>{error}</span>
            <Button size="sm" variant="secondary" onClick={() => setRetryCount((count) => count + 1)}>Retry</Button>
          </div>
        )}

        {!loading && !error && runs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-default bg-surface-raised p-8 text-center text-content-muted">
            No ReaganAI screenshots have been uploaded yet.
          </div>
        )}

        <div className="space-y-8">
          {runs.map((run) => (
            <section key={run.runId} className="rounded-2xl border border-border-default bg-surface-raised p-5 shadow-card">
              <button
                type="button"
                onClick={() => setExpandedRunId((current) => (current === run.runId ? null : run.runId))}
                className="w-full flex flex-wrap items-center justify-between gap-3 text-left"
                aria-expanded={expandedRunId === run.runId}
              >
                <div>
                  <h2 className="text-lg font-semibold text-content-primary">{run.runId}</h2>
                  <p className="text-sm text-content-muted">
                    {run.artifacts.length} screenshots · {new Date(run.updatedAt).toLocaleString()}
                  </p>
                </div>
                <span className="text-xs text-content-secondary">
                  {expandedRunId === run.runId ? "Collapse" : "Expand"}
                </span>
              </button>

              {expandedRunId === run.runId && (
                <div className="mt-5">
                  {run.account && (
                    <div className="mb-5 rounded-xl border border-status-active-border bg-status-active-bg p-4">
                      <p className="text-sm font-semibold text-status-active-text mb-3">Generated ReaganAI Account</p>
                      <div className="grid gap-3 md:grid-cols-3">
                        <div>
                          <p className="text-xs text-content-muted">Email</p>
                          <p className="font-mono text-sm text-content-primary break-all">{run.account.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-content-muted">Username</p>
                          <p className="font-mono text-sm text-content-primary break-all">{run.account.username}</p>
                        </div>
                        <div>
                          <p className="text-xs text-content-muted">Password</p>
                          <p className="font-mono text-sm text-content-primary break-all">{run.account.password}</p>
                        </div>
                      </div>
                      {run.account.inviteUrl && (
                        <a
                          href={run.account.inviteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block mt-3 text-xs text-status-active-text hover:text-content-primary underline"
                        >
                          Open invite link
                        </a>
                      )}
                    </div>
                  )}

                  {run.artifacts.length === 0 ? (
                    <p className="text-sm text-content-muted">No screenshots in this run yet.</p>
                  ) : (
                    <div className="grid gap-5 md:grid-cols-2">
                      {run.artifacts.map((artifact) => {
                        const key = artifactKey(run.runId, artifact.fileName);
                        return (
                          <figure key={key} className="rounded-xl border border-border-default bg-surface-base overflow-hidden">
                            <div className="aspect-video bg-surface-overlay flex items-center justify-center">
                              {images[key] ? (
                                <img
                                  src={images[key]}
                                  alt={`${run.runId} ${artifact.fileName}`}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <span role="status" className="text-sm text-content-muted">Loading image…</span>
                              )}
                            </div>
                            <figcaption className="p-4 border-t border-border-default">
                              <p className="font-mono text-sm text-content-primary break-all">{artifact.fileName}</p>
                              <p className="text-xs text-content-muted mt-1">
                                {formatBytes(artifact.size)} · {new Date(artifact.updatedAt).toLocaleString()}
                              </p>
                            </figcaption>
                          </figure>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>
          ))}
        </div>
    </div>
  );
}
